import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Eye,
  Pencil,
  MoreHorizontal,
  Search,
  LayoutGrid,
  List,
  Frown,
  Plus,
  Sparkles,
  FileText,
  CheckCircle2,
  Pause,
  Calendar
} from 'lucide-react';
import { editorService, getApiErrorMessage } from '../services/api';
import { getUser } from '../services/auth';
import { useBrandedDialog } from './BrandedDialog';
import './EditorList.css';

const getStyleColors = (style) => {
  const map = {
    'Formal': { bg: '#e0f2fe', color: '#0284c7' },
    'Casual': { bg: '#ccfbf1', color: '#0d9488' },
    'Technical': { bg: '#f1f5f9', color: '#475569' },
    'Creative': { bg: '#fce7f3', color: '#db2777' },
    'Journalistic': { bg: '#ffedd5', color: '#ea580c' },
    'Academic': { bg: '#f3e8ff', color: '#9333ea' },
  };
  return map[style] || { bg: '#f1f5f9', color: '#475569' };
};

const getInitials = (name) => {
  if (!name) return 'ED';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const formatAuditUser = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value.userName || [value.firstName, value.lastName].filter(Boolean).join(' ') || value.name || value._id || '';
};

const ITEMS_PER_PAGE = 12;
const ORG_ADMIN_ERROR = 'You are not admin of this org';

const EditorList = () => {
  const [editors, setEditors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active'); // active, disabled, all
  const [sortBy, setSortBy] = useState('recent');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [permissionDialog, setPermissionDialog] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const { alert } = useBrandedDialog();
  const user = getUser();
  const canManageResourcePrompts = Boolean(user?.permissions?.canManageResourcePrompts);

  useEffect(() => {
    fetchEditors();
  }, []);

  useEffect(() => {
    const queryFilter = new URLSearchParams(location.search).get('filter') || 'all';
    setActiveFilter(queryFilter);
    setCurrentPage(1);
  }, [location.search]);

  useEffect(() => {
    const handleOutsideClick = () => setOpenDropdownId(null);
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, []);

  const fetchEditors = async () => {
    try {
      setLoading(true);
      const response = await editorService.getAll();
      const rows = response.data || [];
      setEditors(rows);
      setError(null);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load Editors.'));
      console.error('Error fetching Editors:', err);
    } finally {
      setLoading(false);
    }
  };

  const showMutationError = (err, fallback) => {
    const message = err.response?.data?.error || err.message || fallback;
    if (err.response?.status === 403 || message.toLowerCase().includes('admin')) {
      setPermissionDialog(ORG_ADMIN_ERROR);
      return;
    }
    alert({ title: fallback, message, tone: 'danger' });
  };

  const toggleEditorStatus = async (e, editor) => {
    e.stopPropagation();
    try {
      const newStatus = !editor.isActive;
      await editorService.toggleStatus(editor._id, newStatus);
      setEditors(prev => prev.map(ed => ed._id === editor._id ? { ...ed, isActive: newStatus } : ed));
    } catch (err) {
      console.error('Error toggling editor status:', err);
      showMutationError(err, 'Failed to update editor status');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredEditors = editors.filter(editor => {
    const matchesSearch = editor.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          (editor.style && editor.style.toLowerCase().includes(searchQuery.toLowerCase()));
    
    let matchesStatus = true;
    if (statusFilter === 'active') matchesStatus = editor.isActive !== false;
    if (statusFilter === 'disabled') matchesStatus = editor.isActive === false;

    let matchesStyle = true;
    if (activeFilter !== 'all') {
       matchesStyle = editor.style === activeFilter;
    }
    
    return matchesSearch && matchesStatus && matchesStyle;
  });

  const sortedEditors = [...filteredEditors].sort((a, b) => {
    if (sortBy === 'recent') return new Date(b.createdDate) - new Date(a.createdDate);
    if (sortBy === 'oldest') return new Date(a.createdDate) - new Date(b.createdDate);
    if (sortBy === 'name') return a.name.localeCompare(b.name);
    return 0;
  });

  const totalPages = Math.ceil(sortedEditors.length / ITEMS_PER_PAGE) || 1;
  const paginatedEditors = sortedEditors.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const totalCount = editors.length;
  const activeCount = editors.filter(e => e.isActive !== false).length;
  const disabledCount = editors.filter(e => e.isActive === false).length;

  const styles = [...new Set(editors.map(e => e.style).filter(Boolean))];

  if (loading) {
    return (
      <div className="editorlist-page">
        <div className="editorlist-main">
          <div className="app-loading-container">
            <div className="app-loading-spinner" />
            <p>Loading Editors...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="editorlist-page">
        <div className="editorlist-main">
          <div className="editorlist-error">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="editorlist-page">
      <div className="editorlist-main">
        {permissionDialog && (
          <div className="editorlist-modal-backdrop" role="presentation" onMouseDown={() => setPermissionDialog('')}>
            <div
              className="editorlist-permission-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="editor-permission-dialog-title"
              onMouseDown={event => event.stopPropagation()}
            >
              <h2 id="editor-permission-dialog-title">Permission required</h2>
              <p>{permissionDialog}</p>
              <button className="editorlist-btn-generate" onClick={() => setPermissionDialog('')}>
                OK
              </button>
            </div>
          </div>
        )}
        {/* Sticky Top Area */}
        <div className="editorlist-sticky-top">
          {/* Header */}
          <div className="editorlist-header">
            <div className="editorlist-title-area">
              <h1 className="editorlist-title">Content Editors</h1>
              <p className="editorlist-subtitle">
                Manage AI editors responsible for reviewing and refining AI-generated articles.
              </p>
            </div>
            <div className="editorlist-header-actions">
              {canManageResourcePrompts && (
                <button className="editorlist-btn-ghost" onClick={() => navigate('/prompts')}>
                  <Sparkles size={15} strokeWidth={2} />
                  Prompt Manager
                </button>
              )}
              <button className="editorlist-btn-generate" onClick={() => navigate('/editor/new')}>
                <Plus size={15} strokeWidth={2.5} />
                Add Editor
              </button>
            </div>
          </div>

          {/* Toolbar */}
          <div className="editorlist-toolbar">
            <div className="editorlist-search-wrap">
              <Search className="editorlist-search-icon" size={16} strokeWidth={2} />
              <input
                className="editorlist-search"
                type="text"
                placeholder="Search editors by name or expertise..."
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              />
            </div>

            <div className="editorlist-toolbar-right">
              <select
                className="editorlist-select"
                value={activeFilter}
                onChange={e => { setActiveFilter(e.target.value); setCurrentPage(1); }}
              >
                <option value="all">Language Style</option>
                {styles.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>

              <select className="editorlist-select" value={sortBy} onChange={e => { setSortBy(e.target.value); setCurrentPage(1); }}>
                <option value="recent">Sort by: Most Recent</option>
                <option value="oldest">Sort by: Oldest First</option>
                <option value="name">Sort by: Name A–Z</option>
              </select>

              <div className="editorlist-view-toggle">
                <button
                  className={`editorlist-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                  onClick={() => setViewMode('grid')}
                  title="Grid view"
                >
                  <LayoutGrid size={16} strokeWidth={2} />
                </button>
                <button
                  className={`editorlist-view-btn ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => setViewMode('list')}
                  title="List view"
                >
                  <List size={16} strokeWidth={2} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="editorlist-stats-row">
          <div 
            className={`editorlist-stat-card ${statusFilter === 'all' ? 'active-stat' : ''}`}
            onClick={() => { setStatusFilter('all'); setCurrentPage(1); }}
            style={{ cursor: 'pointer', border: statusFilter === 'all' ? '1px solid #9333ea' : '1px solid #e5ebe8', boxShadow: statusFilter === 'all' ? '0 0 0 2px rgba(147, 51, 234, 0.1)' : '0 4px 12px rgba(0,0,0,0.02)' }}
          >
            <div className="stat-icon-wrap" style={{ background: '#f3e8ff', color: '#9333ea' }}>
              <FileText size={18} strokeWidth={2.2} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Total Editors</p>
              <p className="stat-value">{totalCount}</p>
            </div>
          </div>
          <div 
            className={`editorlist-stat-card ${statusFilter === 'active' ? 'active-stat' : ''}`}
            onClick={() => { setStatusFilter('active'); setCurrentPage(1); }}
            style={{ cursor: 'pointer', border: statusFilter === 'active' ? '1px solid #16a34a' : '1px solid #e5ebe8', boxShadow: statusFilter === 'active' ? '0 0 0 2px rgba(22, 163, 74, 0.1)' : '0 4px 12px rgba(0,0,0,0.02)' }}
          >
            <div className="stat-icon-wrap" style={{ background: '#dcfce7', color: '#16a34a' }}>
              <CheckCircle2 size={18} strokeWidth={2.2} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Active Editors</p>
              <p className="stat-value">{activeCount}</p>
            </div>
          </div>
          <div 
            className={`editorlist-stat-card ${statusFilter === 'disabled' ? 'active-stat' : ''}`}
            onClick={() => { setStatusFilter('disabled'); setCurrentPage(1); }}
            style={{ cursor: 'pointer', border: statusFilter === 'disabled' ? '1px solid #ea580c' : '1px solid #e5ebe8', boxShadow: statusFilter === 'disabled' ? '0 0 0 2px rgba(234, 88, 12, 0.1)' : '0 4px 12px rgba(0,0,0,0.02)' }}
          >
            <div className="stat-icon-wrap" style={{ background: '#ffedd5', color: '#ea580c' }}>
              <Pause size={18} strokeWidth={2.2} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Disabled Editors</p>
              <p className="stat-value">{disabledCount}</p>
            </div>
          </div>
        </div>

        {/* Empty State */}
        {filteredEditors.length === 0 ? (
          <div className="editorlist-empty">
            <div className="editorlist-empty-icon">
              <Frown size={48} strokeWidth={1.5} color="#9ca3af" />
            </div>
            <p className="editorlist-empty-title">No Editors found</p>
            <p className="editorlist-empty-desc">
              {searchQuery ? 'Try adjusting your search.' : 'Create your first Editor to get started!'}
            </p>
            <button className="editorlist-btn-generate" onClick={() => navigate('/editor/new')}>
              <Plus size={15} strokeWidth={2.5} />
              Add Editor
            </button>
          </div>
        ) : (
          <>
            {/* Grid / List */}
            <div className={viewMode === 'grid' ? 'editorlist-grid' : 'editorlist-list'}>
              {paginatedEditors.map(editor => {
                const styleColors = getStyleColors(editor.style);
                const isActive = editor.isActive !== false;

                // Derive a snippet from the prompt if no description
                let snippet = editor.prompt || '';
                if (snippet.length > 90) {
                  snippet = snippet.substring(0, 90) + '...';
                } else if (!snippet) {
                  snippet = 'An AI editor responsible for refining generated content.';
                }

                return (
                  <div
                    key={editor._id}
                    className={`editor-card ${!isActive ? 'editor-card-disabled' : ''}`}
                    style={{ zIndex: openDropdownId === editor._id ? 10 : 1 }}
                  >
                    {/* Header */}
                    <div className="editor-card-header">
                      <div className="editor-card-icon-name">
                        <div 
                          className="editor-card-avatar"
                          style={{ background: styleColors.bg, color: styleColors.color }}
                        >
                          {getInitials(editor.name)}
                        </div>
                        <div className="editor-card-name-wrapper">
                          <h3 className="editor-card-name">{editor.name}</h3>
                          <span className="editor-badge" style={{ color: styleColors.color }}>
                            {editor.style || 'General'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Body */}
                    <div className="editor-card-body">
                      <p className="editor-card-desc">
                        {snippet}
                      </p>
                    </div>

                    {/* Card Footer */}
                    <div className="editor-card-footer">
                      {/* Meta */}
                      <div className="editor-card-meta">
                        <span className="editor-status-dot">
                          <span className="editor-status-indicator" style={{ background: isActive ? '#10b981' : '#f59e0b' }} />
                          {isActive ? 'Active' : 'Disabled'}
                        </span>
                        <span style={{ margin: '0 0.5rem', color: '#cbd5e1' }}>•</span>
                        <span className="meta-item" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Calendar size={13} strokeWidth={2} />
                          Created: {formatDate(editor.createdDate)}
                        </span>
                        {(formatAuditUser(editor.createdBy) || formatAuditUser(editor.updatedBy)) && (
                          <span className="editor-card-audit-line">
                            {formatAuditUser(editor.createdBy) && <span>Created by {formatAuditUser(editor.createdBy)}</span>}
                            {formatAuditUser(editor.updatedBy) && <span>Updated by {formatAuditUser(editor.updatedBy)}</span>}
                          </span>
                        )}
                      </div>

                      {/* Footer Actions */}
                      <div className="editor-card-actions">
                        <button
                          className="editor-action-btn"
                          onClick={e => { e.stopPropagation(); navigate(`/editor/${editor._id}`); }}
                        >
                          <Eye size={15} strokeWidth={2} />
                          View
                        </button>
                        <button
                          className="editor-action-btn"
                          onClick={e => { e.stopPropagation(); navigate(`/editor/${editor._id}/edit`); }}
                        >
                          <Pencil size={15} strokeWidth={2} />
                          Edit
                        </button>
                        <div className="editor-action-more-wrapper" style={{ position: 'relative' }}>
                          <button
                            className="editor-action-btn editor-action-more"
                            onClick={e => { e.stopPropagation(); setOpenDropdownId(openDropdownId === editor._id ? null : editor._id); }}
                            title="More options"
                          >
                            <MoreHorizontal size={15} strokeWidth={2} />
                          </button>
                          {openDropdownId === editor._id && (
                            <div className="editor-dropdown-menu" style={{ position: 'absolute', right: 0, top: '100%', marginTop: '4px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', zIndex: 10, minWidth: '120px', padding: '4px 0' }}>
                              <button
                                style={{ width: '100%', padding: '0.5rem 1rem', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.875rem' }}
                                onClick={(e) => {
                                  toggleEditorStatus(e, editor);
                                  setOpenDropdownId(null);
                                }}
                              >
                                {isActive ? 'Deactivate' : 'Activate'}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            <div className="editorlist-footer">
              <span className="editorlist-count">
                Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–
                {Math.min(currentPage * ITEMS_PER_PAGE, filteredEditors.length)} of {filteredEditors.length} Editors
              </span>
              {totalPages > 1 && (
                <div className="editorlist-pagination">
                  <button
                    className="editorlist-page-btn"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    ‹
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      className={`editorlist-page-btn ${page === currentPage ? 'active' : ''}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    className="editorlist-page-btn"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    ›
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EditorList;
